import dayjs, { Dayjs } from 'dayjs'
import {
  AspectRatio,
  AtButtonDimensions,
  AtButtonProps,
  AtButtonTypes,
  AtEyebrowLinkTarget,
  AtIconInformativeProps,
  AtLinkProps,
  AtLinkTarget,
  AtLinkType,
  FormField,
  Holiday,
  Media,
  MlNotificationProps,
  MlSnackbarProps,
  MlSpaceCardProps,
  Month,
  NotificationType,
  OrContentBlockAProps,
  OrSimpleTextBlockProps,
  formatCurrency,
  holidayTemplate,
  yearTemplate,
} from '@studio/atomic-ui'
import { LayoutProps } from '../../components/layout'
import { googleMapsApiKey } from '../../env'
import type { TmBuildingDetailProps } from '../../templates/tm-building-detail'
import type { Building } from '../../types/building'
import type { TmCheckoutProps } from '../../templates/tm-checkout'
import type { TmContactProps } from '../../templates/tm-contact'
import type { TmContactV2Props } from '../../templates/tm-contact-v2'
import type { MlFormSuccessProps } from '../../components/ml-form-success'
import type { TmPdpProps } from '../../templates/tm-pdp'
import type { TmPlpProps } from '../../templates/tm-plp'
import { PlpSortStrategy } from '../../services/api/types'
import { BffProduct, ProductError, ProductSource } from '../../types/product'
import { addBasePath, addExternalUrlProtocol, isExternalURL } from '../../utils/helpers'
import { priceLabel, productType } from '../../utils/spaces'
import { getSelectedTemplateVariant } from '../../utils/ab-testing'

import {
  SanityAtButton,
  SanityAtFormField,
  SanityAtIconInformative,
  SanityAtImage,
  SanityAtLink,
  SanityBuilding,
  SanityMlFormSuccess,
  SanityMlNotification,
  SanityMlSnackbar,
  SanityNonBookableDates,
  SanityOrBaseContent,
  SanityOrFlexibleBlock,
  SanityOrTabs,
  SanityPage,
  SanitySmProduct,
  SanityTemplate,
  SanityTmCheckout,
  SanityTmContact,
  SanityTmContactV2,
  SanityTmFlexible,
  SanityTmProductListing,
} from './types'
import { mapSanityOrBaseContentToOrBaseContent, mapTmFlexibleProps } from '../../templates/tm-flexible/mapper'
import { buildSanityImageSrcset, mapImage } from './client'
import { getProductById } from '../bff/products'
import { priceSanityField } from '../../components/product-showcase/showcase-utils'

export const mapLink = ({
  href,
  label,
  leftIcon,
  rightIcon,
  ariaLabel,
  variant,
  target,
  hasIcon,
}: SanityAtLink): AtLinkProps => ({
  type: variant === 'inline' ? AtLinkType.INLINE : variant === 'secondary' ? AtLinkType.SECONDARY : AtLinkType.DEFAULT,
  title: label ?? '',
  actionTarget: addBasePath(href) ?? '#',
  target: target ?? (isExternalURL(href) ? AtLinkTarget.BLANK : AtLinkTarget.SELF),
  ...(ariaLabel ? { ariaLabel } : {}),
  ...(leftIcon?.src && hasIcon ? { iconLeft: leftIcon.src } : {}),
  ...(rightIcon?.src && hasIcon ? { iconRight: rightIcon.src } : {}),
})

export const mapButton = ({
  label,
  leftIcon,
  rightIcon,
  actionValue,
  actionType,
  variant,
  target,
}: SanityAtButton): AtButtonProps => ({
  title: label ?? '',
  size: AtButtonDimensions.LARGE,
  type: variant as AtButtonTypes,
  ...(actionType === 'openUrl'
    ? {
        openUrl: addBasePath(actionValue !== null ? actionValue : '') ?? '#',
        target: target ?? '_self',
      }
    : {}),
  ...(leftIcon?.src ? { iconLeft: leftIcon.src } : {}),
  ...(rightIcon?.src ? { iconRight: rightIcon.src } : {}),
})

export const mapNonBookableDatesToHoliday = (sanityNonBookableDates?: SanityNonBookableDates | null): Holiday => {
  if (!sanityNonBookableDates || !sanityNonBookableDates.nonBookableDates) {
    return holidayTemplate()
  }

  const getDatesBetween = (startDate: dayjs.Dayjs, endDate: dayjs.Dayjs) => {
    const dates = []
    let currentDate = startDate
    const addDays = (currentDate: dayjs.Dayjs, days: number) => {
      const date = currentDate
      return date.add(days, 'day')
    }
    while (currentDate <= endDate) {
      dates.push(currentDate)
      currentDate = addDays(currentDate, 1)
    }
    return dates
  }

  const transformDatesToHoliday = (dates: Dayjs[]): Holiday => {
    const holiday: Holiday = holidayTemplate()

    dates?.forEach((date) => {
      const year = date.get('year')
      const month = (date.get('month') + 1) as Month
      const day = date.date()

      if (!holiday[year]) {
        holiday[year] = yearTemplate()
      }
      holiday[year][month].push(day)
    })

    return holiday
  }

  const datesArray = sanityNonBookableDates.nonBookableDates.dates
    ?.map((d) => (d.endDate ? getDatesBetween(dayjs(d.startDate), dayjs(d.endDate)) : dayjs(d.startDate)))
    .flat()

  return transformDatesToHoliday(datesArray)
}

const resolveCheckboxContainer = (insideFlexTemplate: boolean, insideContainer: boolean): string => {
  if (insideFlexTemplate) return 'mb-2'
  if (insideContainer) return ''
  return 'mt-8 lg:mt-10 mb-12'
}

type FormFieldVariantOverrides = {
  checkboxContainer?: string
  textAreaContainer?: string
  dropdownContainer?: string
}

const FORM_FIELD_VARIANT_OVERRIDES: Record<string, FormFieldVariantOverrides> = {
  'contact-v2': {
    checkboxContainer: 'mt-4',
    textAreaContainer: 'mt-6 mb-2 w-full',
    dropdownContainer: 'mb-6 w-full',
  },
}

type MapFormFieldContext = {
  insideContainer?: boolean
  insideForm?: boolean
  insideFlexTemplate?: boolean
  variant?: keyof typeof FORM_FIELD_VARIANT_OVERRIDES
}

export const mapFormField = (
  props: SanityAtFormField | SanityOrTabs,
  { insideContainer = false, insideForm = false, insideFlexTemplate = false, variant }: MapFormFieldContext = {}
): FormField => {
  const overrides = variant ? FORM_FIELD_VARIANT_OVERRIDES[variant] ?? {} : {}
  if ('tabs' in props)
    return {
      type: 'tab',
      ...(props.fieldName ? { name: props.fieldName } : {}),
      tabs:
        props.tabs?.map((tab) => ({
          label: tab.label,
          ...(tab.icon ? { icon: tab.icon?.src } : {}),
          content: tab.content.map((c) =>
            mapFormField(c as SanityAtFormField, { insideContainer: true, insideFlexTemplate, variant })
          ),
        })) ?? [],
    }
  if (props.inputType === 'divider')
    return { type: 'divider', className: props.dividerVariant === 'compact' ? 'my-6' : 'my-12' }

  if (props.inputType === 'grid')
    return {
      type: 'grid',
      ...(props.label ? { label: props.label } : {}),
      fields:
        props.grid?.map((c) =>
          mapFormField(c, { insideContainer: true, insideForm: true, insideFlexTemplate, variant })
        ) ?? [],
    }

  if (!props.fieldName)
    throw new Error(
      `Unspecified internal name, please set an internal name within Sanity to use the ${props.label} field`
    )

  switch (props.inputType) {
    case 'checkbox':
      return {
        type: 'checkbox',
        name: props.fieldName,
        label: props.label ?? '',
        ...(props.placeholder ? { description: props.placeholder } : {}),
        required: !!props.isRequired,
        container: overrides.checkboxContainer ?? resolveCheckboxContainer(insideFlexTemplate, insideContainer),
      }
    case 'select':
      return {
        type: 'dropdown',
        name: props.fieldName,
        ...(props.label ? { label: props.label } : {}),
        ...(props.placeholder ? { placeholder: props.placeholder } : {}),
        required: !!props.isRequired,
        options: props.options?.map(({ Label, Value }) => ({ label: Label, value: Value })) ?? [],
        ...(props.errorText ? { errorText: props.errorText } : {}),
        ...(props.helperText ? { helperText: props.helperText } : {}),
        container: overrides.dropdownContainer ?? (insideForm ? '' : 'my-8 lg:w-[60%]'),
      }
    case 'radio':
      throw new Error('Unsupported field type, needs to be added into form component')
    case 'text-area':
      return {
        type: 'text-area',
        name: props.fieldName,
        required: !!props.isRequired,
        hideLimitHint: !props.showCharacterLimit,
        ...(props.placeholder ? { placeholder: props.placeholder } : {}),
        ...(props.label ? { label: props.label } : {}),
        ...(props.errorText ? { errorText: props.errorText } : {}),
        ...(props.helperText ? { helperText: props.helperText } : {}),
        ...(props.maxLength ? { maxLength: props.maxLength } : {}),
        ...(props.minLength ? { minLength: props.minLength } : {}),
        ...(props.icon ? { icon: props.icon?.src } : {}),
        ...(props.collapsible ? { collapsible: true, defaultExpanded: !!props.defaultExpanded } : {}),
        container: overrides.textAreaContainer ?? 'mt-8 lg:w-[77%]',
        className: 'h-32',
      }
    case 'text':
      return {
        type: 'text',
        name: props.fieldName,
        required: !!props.isRequired,
        hideLimitHint: !props.showCharacterLimit,
        ...(props.placeholder ? { placeholder: props.placeholder } : {}),
        ...(props.label ? { label: props.label } : {}),
        ...(props.errorText ? { errorText: props.errorText } : {}),
        ...(props.helperText ? { helperText: props.helperText } : {}),
        ...(props.maxLength ? { maxLength: props.maxLength } : {}),
        ...(props.minLength ? { minLength: props.minLength } : {}),
        ...(props.icon ? { icon: props.icon?.src } : {}),
        validator: props.validator ?? 'none',
      }
    case 'label':
      return {
        type: 'label',
        name: props.fieldName,
        label: props.label ?? '',
        ...(props.labelClassName ? { className: props.labelClassName } : {}),
      }
    case 'calendar':
      return {
        type: 'calendar',
        name: props.fieldName,
        required: !!props.isRequired,
        ...(props.label ? { label: props.label } : {}),
        ...(props.errorText ? { errorText: props.errorText } : {}),
        ...(props.helperText ? { helperText: props.helperText } : {}),
        ...(props.calendarSubCheckboxFieldName ? { subCheckboxName: props.calendarSubCheckboxFieldName } : {}),
        ...(props.calendarSubCheckboxLabel ? { subCheckboxLabel: props.calendarSubCheckboxLabel } : {}),
      }
  }
}

export const mapSnackbar = ({
  content,
  status,
  buttonLabel,
}: SanityMlSnackbar): Omit<MlSnackbarProps, 'dismiss' | 'visible' | 'className'> => {
  let type: NotificationType
  switch (status) {
    case 'warning':
    case 'error':
      type = 'negative'
      break
    case 'informative':
      type = 'informative'
      break
    default:
    case 'positive':
      type = 'positive'
      break
  }

  return {
    buttonLabel: buttonLabel ?? 'Dismiss',
    message: content,
    type,
  }
}

export const mapFormSuccess = ({
  heading,
  subheading,
  ctaLabel,
  ctaHref,
}: SanityMlFormSuccess): MlFormSuccessProps => ({
  heading,
  ...(subheading ? { subheading } : {}),
  ...(ctaLabel ? { ctaLabel } : {}),
  ...(ctaHref ? { ctaHref } : {}),
})

export const mapNotification = ({
  linkLabel,
  message,
  notificationType,
  title,
}: SanityMlNotification): Omit<MlNotificationProps, 'className'> => {
  let type: NotificationType
  switch (notificationType) {
    case 'warning':
      type = 'informative'
      break
    case 'error':
      type = 'negative'
      break
    default:
    case 'success':
      type = 'positive'
      break
  }

  return {
    title,
    body: message,
    type,
    link: {
      title: linkLabel ?? '',
    },
  }
}

export const mapLayoutProps = ({
  title,
  header,
  footer,
  pageType,
  id,
  abTestingConfiguration,
  moduleABTestingConfiguration,
}: SanityPage<unknown>): LayoutProps => {
  const mapSanityAtLinkToNavbarLink = (link: SanityAtLink) => ({
    href: addBasePath(link?.href ?? '') ?? '#',
    label: link?.label ?? '',
    target: link?.target || (isExternalURL(link?.href ?? '') ? AtLinkTarget.BLANK : AtLinkTarget.SELF),
  })

  let resolvedTitle = title
  let resolvedHeader = header
  let resolvedFooter = footer
  if (abTestingConfiguration?.active && abTestingConfiguration.pageVariants?.length) {
    const selectedVariant = getSelectedTemplateVariant(abTestingConfiguration) || undefined
    if (selectedVariant && !selectedVariant.controlVariant) {
      // Only apply overrides for non-control variants
      resolvedTitle = selectedVariant.title ?? title
      resolvedHeader = selectedVariant.header ?? header
      resolvedFooter = selectedVariant.footer ?? footer
    }
  }

  return {
    title: resolvedTitle,
    pageType: pageType ?? '',
    id: id ?? '',
    ...(abTestingConfiguration ? { abTestingConfiguration: abTestingConfiguration } : {}),
    ...(moduleABTestingConfiguration ? { moduleABTestingConfiguration: moduleABTestingConfiguration } : {}),
    ...(resolvedHeader
      ? {
          navbar: {
            logo: {
              src: resolvedHeader.logo.src,
              alt: resolvedHeader.logo.alt ?? '',
            },
            links:
              resolvedHeader.navigationLinks
                ?.filter((x) => x)
                .map((props) => {
                  if ('dropdownLinks' in props) {
                    return {
                      type: 'dropdown',
                      title: props.title,
                      dropdownLinks: props.dropdownLinks?.map((link) => mapSanityAtLinkToNavbarLink(link)) ?? [],
                    }
                  }
                  return { type: 'navbarLink', ...mapSanityAtLinkToNavbarLink(props) }
                }) ?? [],
            buttons: resolvedHeader.ctaButtons?.filter((x) => x).map(mapButton) ?? [],
            checkoutHref: resolvedHeader.alternateLink !== null ? addBasePath(resolvedHeader.alternateLink?.href) : '', // only output when populated
          },
        }
      : {}),
    ...(resolvedFooter
      ? {
          footer: {
            logo: {
              src: resolvedFooter.logo.src,
              alt: resolvedFooter.logo.alt ?? '',
            },
            contact: resolvedFooter.contact,
            copyright: resolvedFooter.copyright,
            links: resolvedFooter.links.map(mapLink),
            secondaryLinks: resolvedFooter.secondaryLinks.map(mapLink),
            socialLinks: resolvedFooter.socialMedias?.filter((x) => x).map(mapLink),
          },
        }
      : {}),
  }
}

export const mapContactProps = (
  {
    contactImage,
    contactInfo,
    description,
    serverErrorNotification,
    multipleFieldsErrorNotification,
    singleFieldErrorNotification,
    contactForm,
    snackbarStatus,
  }: SanityTmContact,
  title: string
): Partial<TmContactProps> => {
  const serverErrorSource = contactForm?.serverErrorNotification ?? serverErrorNotification
  const multipleFieldErrorSource = contactForm?.multipleFieldsErrorNotification ?? multipleFieldsErrorNotification
  const fieldErrorSource = contactForm?.singleFieldErrorNotification ?? singleFieldErrorNotification
  const successSnackbarSource = contactForm?.successSnackbar ?? snackbarStatus

  return {
    title,
    description: description ?? '',
    ...(contactImage
      ? {
          info: {
            image: contactImage,
            content: contactInfo,
          },
        }
      : {}),
    ...(serverErrorSource ? { serverError: mapNotification(serverErrorSource) } : {}),
    ...(multipleFieldErrorSource ? { multipleFieldError: mapNotification(multipleFieldErrorSource) } : {}),
    ...(fieldErrorSource ? { fieldError: mapNotification(fieldErrorSource) } : {}),
    ...(successSnackbarSource ? { success: mapSnackbar(successSnackbarSource) } : {}),
    ...(contactForm
      ? {
          form: {
            id: contactForm.id ?? '',
            name: contactForm.name ?? '',
            skipCtaBar: true,
            fields: [
              ...contactForm.fields.map((c) => mapFormField(c)),
              {
                type: 'submit',
                label: contactForm.submitButton?.label ?? '',
                className: 'lg:w-[60%]',
              },
            ],
          },
        }
      : {}),
  }
}

export const mapContactV2Props = (
  {
    title,
    description,
    submitButtonLabel,
    stickySubmitButton,
    inquiryTabs,
    formSuccess,
    successNotification,
    serverErrorNotification,
    singleFieldErrorNotification,
    multipleFieldsErrorNotification,
  }: SanityTmContactV2,
  pageTitle: string
): TmContactV2Props => ({
  title: pageTitle || title,
  description: description ?? '',
  submitButtonLabel: submitButtonLabel ?? 'Submit',
  stickySubmitButton: stickySubmitButton ?? true,
  tabs: (inquiryTabs ?? []).map((tab) => ({
    tabKey: tab.tabKey,
    tabLabel: tab.tabLabel,
    ...(tab.tabDescription ? { tabDescription: tab.tabDescription } : {}),
    tabIcon: tab.tabIcon,
    ...(tab.form
      ? {
          form: {
            id: tab.form.id ?? '',
            name: tab.form.name ?? '',
            skipCtaBar: true,
            fields: tab.form.fields.map((c) => mapFormField(c, { variant: 'contact-v2' })),
          },
        }
      : {}),
  })),
  ...(formSuccess ? { formSuccess: mapFormSuccess(formSuccess) } : {}),
  ...(successNotification ? { success: mapSnackbar(successNotification) } : {}),
  ...(serverErrorNotification ? { serverError: mapNotification(serverErrorNotification) } : {}),
  ...(singleFieldErrorNotification ? { fieldError: mapNotification(singleFieldErrorNotification) } : {}),
  ...(multipleFieldsErrorNotification ? { multipleFieldError: mapNotification(multipleFieldsErrorNotification) } : {}),
})

const mapCheckoutProps = ({
  title,
  steps,
  confirmation,
  serverErrorNotification,
  holidays,
  singleFieldErrorNotification,
  multipleFieldsErrorNotification,
  bookingUnavailableError,
  declinedCreditCardError,
  redirectButton,
}: SanityTmCheckout & { holidays: SanityNonBookableDates }): Partial<TmCheckoutProps> => ({
  title,
  steps: steps?.map((step) => ({
    id: step.id,
    name: step.name,
    title: step.title,
    fields: step.fields?.map((field) => mapFormField(field)) ?? null,
    submitButton: step.submitButton ? mapButton(step.submitButton) : null,
  })),
  holidays: mapNonBookableDatesToHoliday(holidays),
  ...(redirectButton ? { redirectButton: mapLink(redirectButton) } : {}),
  ...(confirmation ? { confirmation } : {}),
  ...(serverErrorNotification ? { serverError: mapNotification(serverErrorNotification) } : {}),
  ...(singleFieldErrorNotification ? { fieldError: mapNotification(singleFieldErrorNotification) } : {}),
  ...(multipleFieldsErrorNotification ? { multipleFieldError: mapNotification(multipleFieldsErrorNotification) } : {}),
  ...(bookingUnavailableError ? { bookingUnavailableError: mapNotification(bookingUnavailableError) } : {}),
  ...(declinedCreditCardError ? { declinedCreditCardError: mapNotification(declinedCreditCardError) } : {}),
})

const mapInformativeIcon = (highlight: SanityAtIconInformative): AtIconInformativeProps => ({
  icon: highlight.icon?.src ?? '',
  title: highlight.title,
  label: highlight.subtitle ?? highlight.body,
})

const mapOrFlexBlock = (props: SanityOrFlexibleBlock) => {
  if (props.layout === 'flexibleC')
    return {
      type: 'map',
      markers: props.address ? [{ address: props.address }] : [],
      details: props.details?.map(mapInformativeIcon) ?? [],
      apiKey: googleMapsApiKey,
      ...(props.description ? { description: props.description } : {}),
    }

  if (props.layout === 'flexibleB')
    return {
      type: 'directions',
      images: props.images,
      directions: props.directions?.map((label) => ({ label })) ?? [],
      ...(props.shortDescription ? { shortDescription: props.shortDescription } : {}),
      aspectRatio: props.aspectRatio ?? '16:9',
      ...(props.description ? { description: props.description } : {}),
    }

  return {
    type: 'text',
    images: props.images,
    aspectRatio: (props.aspectRatio as AspectRatio) ?? '16:9',
    ...(props.shortDescription ? { shortDescription: props.shortDescription } : {}),
    ...(props.description ? { description: props.description } : {}),
    ...(props.detailsTitle ? { title: props.detailsTitle } : {}),
    ...(props.detailsBody ? { body: props.detailsBody } : {}),
    ...(props.details ? { details: props.details.map(mapInformativeIcon) } : {}),
  }
}

// TODO: Map for other types (see related Sanity query at `./queries/helpers.ts`)
const mapBaseContent = (props: SanityOrBaseContent): OrSimpleTextBlockProps | OrContentBlockAProps | null => {
  switch (props.layout) {
    case 'simpleTextList':
      return {
        contents: props.list?.map(({ subtitle, title }) => ({ title, ...(subtitle ? { subtitle } : {}) })) ?? [],
        ...(props.sectionDescription ? { mainSubtitle: props.sectionDescription } : {}),
        ...(props.sectionTitle ? { mainTitle: props.sectionTitle } : {}),
      } as OrSimpleTextBlockProps
    case 'descriptions':
      return {
        title: props.sectionTitle ?? '',
        media: props.media ? props.media : props.images ?? [],
        contents:
          props.list?.map(({ title, body, link }) => ({
            title,
            body: body ?? '',
            ...(link ? { link: mapLink(link) } : {}),
          })) ?? [],
      } as OrContentBlockAProps
    default:
      return null
  }
}

export type MapSpaceCardOptions = {
  buildings: Building[]
  /** When 'manual', FEATURED badge is shown for products with plpRank. Omit or 'standard' to hide it. */
  sortStrategy?: 'standard' | 'manual'
}

export const mapSpaceCard = (
  product: SanitySmProduct & { backend?: BffProduct | ProductError; price?: number; capacity?: number },
  options: MapSpaceCardOptions
): Omit<MlSpaceCardProps, 'images'> & {
  images: SanityAtImage[]
} => {
  const priceParams = mapCardPriceParams(product)
  const tags = buildSpaceCardTags(product, priceParams, options?.sortStrategy)
  const { instantBookTag: _, ...restPriceParams } = priceParams
  return {
    images: mapSpaceCardsImages(product),
    space: productType[product.spaceType] ?? 'Unknown',
    href: addBasePath(product.slug),
    building: options.buildings.find((b) => b.key === product.building)?.displayName ?? '',
    title: product.productName,
    ...restPriceParams,
    ...(tags.length > 0 ? { tags } : {}),
    details: product?.specialProductDetails?.length
      ? product.specialProductDetails
          .map((item) => ({
            icon: item.icon?.src ?? '',
            label: item.detail ?? '',
          }))
          .filter((item) => item.label)
          .slice(0, 2) // Limit to 2 details
      : [
          ...(product.capacity
            ? [
                {
                  icon: `${addBasePath('group.svg')}`,
                  label: `${product.capacity} ${product.capacity === 1 ? 'person' : 'people'}`,
                  name: 'capacity',
                },
              ]
            : []),
          ...(product.specialProductDetail?.detail
            ? [
                {
                  icon: product.specialProductDetail.icon?.src ?? '',
                  label: product.specialProductDetail.detail,
                },
              ]
            : []),
        ],
  }
}

function buildSpaceCardTags(
  product: SanitySmProduct,
  priceParams: { instantBookTag?: { icon: boolean; label: string; variant?: 'onDemand' }; rate?: string },
  sortStrategy?: 'standard' | 'manual'
): NonNullable<MlSpaceCardProps['tags']> {
  const tags: NonNullable<MlSpaceCardProps['tags']> = []
  const showFeatured =
    sortStrategy === PlpSortStrategy.Manual && product.plpRank != null && product.plpRank >= 1 && product.plpRank <= 3
  if (showFeatured) {
    tags.push({ icon: 'star', label: 'FEATURED', position: 'left' })
  }
  if (priceParams.instantBookTag) {
    tags.push({ ...priceParams.instantBookTag, position: 'right', variant: 'onDemand' as const })
  }
  return tags
}

const mapCardPriceParams = (product: SanitySmProduct) => {
  const isProductABTestingActive = product.spaceABTestingConfig?.active
  const priceRate = isProductABTestingActive && product.priceRate ? product.priceRate : 1

  const price = product[priceSanityField]
  if (product.inquiryOnly && product.inquiryPrice) {
    return {
      rate: [
        product.textBeforePrice,
        `${formatCurrency(product.inquiryPrice, true, { currency: product.inquiryCurrency || 'USD' })}/${
          product.rateType ?? 'month'
        }`,
      ]
        .filter(Boolean)
        .join(' '),
      rateSubtitle: product.textAfterPrice,
    }
  } else if (price) {
    //TODO: maybe its a good idea to move this into the mapping, also if the inquiryOnly-filter is true this can be skipped
    if (!product.inquiryOnly && product.bookableDays > 0 && price) {
      return {
        instantBookTag: {
          icon: true,
          label: 'Instant book',
        },
        rate: priceLabel(price, product.spaceType, priceRate),
      }
    }
    return {}
  }

  return {}
}

const SPACE_CARD_SIZES = '(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 400px'

const mapSpaceCardsImages = (
  product: SanitySmProduct & { backend?: BffProduct | ProductError; price?: number; capacity?: number }
) => {
  const mediaImages =
    product.media
      ?.filter((item) => item.type === 'image')
      .map((item: any) => ({
        alt: item.alt || '',
        caption: item.caption || null,
        credit: item.credit || null,
        asset: item.asset,
        src: item.src,
        dimensions: item.dimensions,
      })) || []

  const raw = mediaImages.length > 0 ? mediaImages : product.images || []

  return raw.map((img: any) => ({
    ...img,
    srcset: img.asset ? buildSanityImageSrcset(img) : undefined,
    sizes: img.asset ? SPACE_CARD_SIZES : undefined,
  }))
}

export const mapPageDetailPageProps = (
  content: SanitySmProduct & { buildingDetails: SanityBuilding },
  backend: BffProduct,
  buildings: Building[]
): TmPdpProps => {
  // @ts-expect-error Image type
  return {
    minimumBookingDuration: content.minimumBookingDuration,
    earliestBookableHours: content.earliestBookableHours,
    inquiryOnly: content.inquiryOnly,
    textBeforeInquiryPrice: content.textBeforePrice,
    textAfterInquiryPrice: content.textAfterPricePdp ?? content.textAfterPrice,
    inquiryPrice: content.inquiryPrice,
    inquiryCurrency: content.inquiryCurrency ?? null,
    inquiryRateType: content.rateType,
    inquireMessageSuccess: content.inquireSuccessMessage
      ? mapSnackbar(content.inquireSuccessMessage)
      : { message: '', type: 'informative', buttonLabel: '' },
    failureMessage: content.failureMessage
      ? mapSnackbar(content.failureMessage)
      : { message: '', type: 'negative', buttonLabel: '' },
    bookableDays: content.bookableDays ?? 0,
    spaceType: content.spaceType,
    id: parseInt(content.platformId),
    building: content.building,
    holidays: mapNonBookableDatesToHoliday(content.buildingDetails),
    source: content.source,
    capacity: content?.capacity ?? null,
    hideCapacityOnPdp: content?.hideCapacityOnPdp,
    prices: backend?.price ?? null,
    availables: backend?.relatedAvailable ?? [],
    isNonStudioLocation: content.buildingDetails?.isNonStudioLocation ?? false,
    description: {
      title: content.productName,
      building: content.building,
      body: content.description,
      subtitles: [
        ...(content?.capacity && !content.hideCapacityOnPdp ? [`Capacity: ${content.capacity}`] : []),
        ...(content?.squareFootage && !content.hideSquareFootageOnPdp ? [`Size: ${content.squareFootage} sq ft`] : []),
      ],
      ...(content.buildingDetails?.isNonStudioLocation
        ? content.buildingDetails?.nonStudioLocationExternalLink
          ? {
              location: {
                title: content.buildingDetails?.nonStudioLocationExternalLink?.label,
                actionTarget: addExternalUrlProtocol(content.buildingDetails?.nonStudioLocationExternalLink?.href),
                target: AtEyebrowLinkTarget.BLANK,
              },
            }
          : null
        : {
            location: {
              title: buildings.find((b) => b.key === content.building)?.displayName ?? '',
              actionTarget: addBasePath(content.building),
            },
          }),
      type: {
        title: content.spaceType in productType ? productType[content.spaceType] : 'Unknown',
        actionTarget: `${addBasePath('spaces')}?type=${content.spaceType}`,
      },
      media: (content.media?.length ? content.media : content.images) as Media[] | SanityAtImage[],
      ...(content.floorplanImage ? { floorPlanImage: content.floorplanImage } : {}),
      informative: content.highlights?.map(mapInformativeIcon) ?? [],
    },
    ...(content.spaceCapabilities
      ? {
          spaceCapabilities: {
            ...mapOrFlexBlock(content.spaceCapabilities),
            mainTitle: content.spaceCapabilities.contentTitle,
          },
        }
      : {}),
    ...(content.additionalServices?.layout === 'simpleTextList'
      ? { additionalServices: mapBaseContent(content.additionalServices) }
      : {}),
    ...(content.location
      ? {
          location: {
            title: content.location.tabsTitle ?? '',
            tabs: content.location.tabs.map((tab) => ({
              label: tab.label,
              content: mapOrFlexBlock(tab.content?.at(0) as SanityOrFlexibleBlock),
            })),
          },
        }
      : {}),
    ...(content.similarSpaces
      ? {
          similarSpaces: {
            type: 'space',
            title: content.similarSpaces.tabsTitle ?? '',
            cards: content.similarSpaces.tabs.map((tab) => ({
              label: tab.label,
              content:
                (tab.content as SanitySmProduct[])?.map((product) => mapSpaceCard(product, { buildings })) ?? null,
            })),
          },
        }
      : {}),
    ...(content.otherSolutions
      ? {
          otherWorkspaces: content.otherSolutions?.map((element) => ({
            images: element.images,
            title: productType[element.spaceType] ?? 'Unknown',
            href: `${addBasePath('spaces')}?type=${element.spaceType}`,
          })),
        }
      : {}),
    ...(content.brandMoment ? { brand: mapSanityOrBaseContentToOrBaseContent(content.brandMoment) } : {}),
  }
}

export const getBffProducts = async (sanityProducts: { platformId: string; source: ProductSource }[]) => {
  // Assume they're all different ids. Test environment could have same ids
  const resp = await Promise.all(
    sanityProducts.map((p) =>
      getProductById({
        id: p.platformId,
        source: p.source,
      })
    ) ?? []
  )

  return resp.filter((result) => !(result !== undefined && 'error' in result)) as BffProduct[]
}

export const mapProductListing = (payload: SanityTmProductListing, title: string): TmPlpProps => {
  const { notFindingTitle, notFindingImage, contactButton } = payload.notFindingCard ?? {}

  return {
    title,
    ...(notFindingTitle && notFindingImage && contactButton
      ? {
          notFoundCard: {
            title: notFindingTitle,
            button: mapButton(contactButton),
            image: mapImage(notFindingImage),
          },
        }
      : {}),
    ...(payload.loadButtonLabel ? { loadMoreLabel: payload.loadButtonLabel } : {}),
    ...(payload.notFoundTitle
      ? {
          notFoundView: {
            title: payload.notFoundTitle,
            subtitle: payload.notFoundText ?? '',
            details:
              payload.contactDetails?.map((d) => ({
                ...(d.icon?.src ? { icon: d.icon.src } : {}),
                label: d.title,
              })) ?? [],
          },
        }
      : {}),
  }
}

export const mapBuildingDetailProps = (template: any, title: string): TmBuildingDetailProps => {
  return {
    template: {
      ...template,
      address: {
        ...template.address,
        directionLink: template.address.directionLink ? mapLink(template.address.directionLink) : null,
      },
      isNonStudioLocation: template.isNonStudioLocation ?? false,
    },
    title,
  }
}

const selectTemplateDataFromABTesting = (
  defaultType: SanityTemplate,
  payload: SanityPage<unknown>
): { type: SanityTemplate; template: unknown; title: string } => {
  if (payload.abTestingConfiguration?.active && payload.abTestingConfiguration.pageVariants?.length) {
    const selectedVariant = getSelectedTemplateVariant(payload.abTestingConfiguration)
    if (selectedVariant && !selectedVariant.controlVariant && selectedVariant.content) {
      // Use variant content type, template, and optional title override
      return {
        type: selectedVariant.content._type as SanityTemplate,
        template: selectedVariant.content,
        title: selectedVariant.title ?? payload.title,
      }
    }
  }

  return {
    type: defaultType,
    template: payload.template,
    title: payload.title,
  }
}

export const mapSelector = (type: SanityTemplate, payload: SanityPage<unknown>, buildings: Building[]) => {
  const {
    type: selectedType,
    template: selectedTemplate,
    title: selectedTitle,
  } = selectTemplateDataFromABTesting(type, payload)

  // Map based on selected type (either page's type or variant's type)
  switch (selectedType) {
    case 'tmVisitorGuide':
    case 'tmGuestList':
      return selectedTemplate
    case 'tmBuildingDetail':
      return mapBuildingDetailProps(selectedTemplate, selectedTitle)
    case 'tmContact':
      return mapContactProps(selectedTemplate as SanityTmContact, selectedTitle)
    case 'tmContactV2':
      return mapContactV2Props(selectedTemplate as SanityTmContactV2, selectedTitle)
    case 'tmCheckout':
      return mapCheckoutProps(selectedTemplate as SanityTmCheckout & { holidays: SanityNonBookableDates })
    case 'tmFlexible':
      return mapTmFlexibleProps(((selectedTemplate as unknown) || []) as SanityTmFlexible, selectedTitle, buildings)
    default:
      return {}
  }
}
